import React, { useEffect, useRef, useState, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Messages } from "primereact/messages";
import { PrimeIcons } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { BreadCrumb } from "primereact/breadcrumb";
import { Divider } from "primereact/divider";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import {
  MoneyTransferService,
  TransferCounterparty,
  TransferMethod,
  TransferVerificationResponse,
  BankDirectoryEntry,
} from "../app_utils/api/MoneyTransferService";
import { detectNetwork, NetworkDetectionResult } from "../app_utils/api/NetworkDetectionService";
import { HOME_ROUTE_PATH, WALLET_ROUTE_PATH } from "../app_utils/route_paths/resolver/PageRoutes";
import { UserSessionUtils } from "../app_utils/utils/UserSessionUtils";
import { formatAmountWithCommas } from "../app_utils/utils/Utils";

const NETWORKS = [
  { label: "MTN", value: "MTN" },
  { label: "Airtel", value: "AIRTEL" },
];

const METHODS = [
  { label: "Mobile Money (MTN / Airtel)", value: "mobile_money" },
  { label: "Bank Transfer", value: "bank" },
];

const generateUuid = (): string => {
  if (typeof crypto !== "undefined" && typeof (crypto as any).randomUUID === "function") {
    return (crypto as any).randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const MoneyTransfers = () => {
  const [records, setRecords] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [transferStats, setTransferStats] = useState<any>(null);
  const [mobileMoneyStats, setMobileMoneyStats] = useState<any>(null);
  const [bankTransferStats, setBankTransferStats] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Form state
  const [method, setMethod] = useState<TransferMethod>("mobile_money");
  const [msisdn, setMsisdn] = useState("");
  const [network, setNetwork] = useState("MTN");
  const [accountName, setAccountName] = useState("");
  const [banks, setBanks] = useState<BankDirectoryEntry[]>([]);
  const [bankCode, setBankCode] = useState<string | null>(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [amount, setAmount] = useState<number | null>(null);
  const [reference, setReference] = useState("");
  const [narration, setNarration] = useState("");

  // Two-step flow state
  const [verification, setVerification] = useState<TransferVerificationResponse | null>(null);
  const [verifiedSignature, setVerifiedSignature] = useState<string>("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [liveState, setLiveState] = useState<string | null>(null);
  const [failureReason, setFailureReason] = useState<string | null>(null);
  const pollRef = useRef<any>(null);
  const networkDetectRef = useRef<any>(null);
  const [detectedNetwork, setDetectedNetwork] = useState<NetworkDetectionResult | null>(null);

  const message = useRef<any>();
  const toast = useRef<Toast>(null);
  const userId = UserSessionUtils.getUserDetails()?.id;

  const counterparty: TransferCounterparty =
    method === "mobile_money"
      ? { msisdn: msisdn.trim(), network, account_name: accountName.trim() || undefined }
      : { bank_code: bankCode || undefined, account_number: accountNumber.trim(), account_name: accountName.trim() || undefined };

  const payloadSignature = JSON.stringify({
    method,
    amount: amount || 0,
    currency: "UGX",
    counterparty,
  });

  const fetchWalletBalance = () => {
    if (!userId) return;
    new BaseApiServiceImpl("/api/v1/wallet-deposits/balance-info/" + userId)
      .getRequestWithJsonResponse({})
      .then((response) => {
        setWalletBalance(response?.data?.balance ?? 0);
      })
      .catch(() => {});
  };

  const fetchTransferStats = async () => {
    if (!userId) return;
    setStatsLoading(true);
    try {
      const [mobileStats, bankStats] = await Promise.all([
        MoneyTransferService.getTransferStats("mobile_money"),
        MoneyTransferService.getTransferStats("bank"),
      ]);
      setMobileMoneyStats(mobileStats);
      setBankTransferStats(bankStats);
    } catch (error) {
      setMobileMoneyStats(null);
      setBankTransferStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchRecords = () => {
    setLoading(true);
    MoneyTransferService.listTransfers({ offset, limit })
      .then((response) => {
        setRecords(response.records);
        setTotalRecords(response.totalRecords);
        setLoading(false);
      })
      .catch(() => {
        setRecords([]);
        setTotalRecords(0);
        setLoading(false);
      });
  };

  const fetchBanks = () => {
    MoneyTransferService.getDirectory("BANK", "UG")
      .then((entries) => setBanks(entries))
      .catch(() => setBanks([]));
  };

  useEffect(() => {
    fetchRecords();
    fetchWalletBalance();
    fetchTransferStats();
    fetchBanks();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, userId]);

  useEffect(() => {
    if (networkDetectRef.current) clearTimeout(networkDetectRef.current);
    if (!msisdn || msisdn.length < 6) {
      setDetectedNetwork(null);
      return;
    }
    networkDetectRef.current = setTimeout(async () => {
      const result = await detectNetwork(msisdn);
      if (result) {
        setDetectedNetwork(result);
        setNetwork(result.network);
        resetVerification();
      } else {
        setDetectedNetwork(null);
      }
    }, 300);
    return () => {
      if (networkDetectRef.current) clearTimeout(networkDetectRef.current);
    };
  }, [msisdn]);

  const resetVerification = () => {
    setVerification(null);
    setVerifiedSignature("");
    setIdempotencyKey(null);
    setLiveState(null);
    setFailureReason(null);
  };

  const validateForm = (): string | null => {
    if (!amount || amount <= 0) return "Enter a valid amount greater than zero.";
    if (amount > walletBalance) return "Amount exceeds your available wallet balance.";
    if (narration.length > 140) return "Narration must not exceed 140 characters.";
    if (method === "mobile_money") {
      if (!msisdn.trim()) return "Enter the recipient's mobile money number.";
      if (!network) return "Select the recipient's network (MTN or Airtel).";
    } else {
      if (!bankCode) return "Select the recipient's bank.";
      if (!accountNumber.trim()) return "Enter the recipient's bank account number.";
    }
    return null;
  };

  const handleRequestVerification = async () => {
    const validationError = validateForm();
    if (validationError) {
      toast.current?.show({ severity: "warn", summary: "Check the form", detail: validationError });
      return;
    }
    setIsVerifying(true);
    try {
      const result = await MoneyTransferService.requestVerification({
        method,
        amount: amount!,
        currency: "UGX",
        counterparty,
        narration: narration.trim() || undefined,
      });
      setVerification(result);
      setVerifiedSignature(payloadSignature);
      setIdempotencyKey(null);
      setLiveState(null);
      toast.current?.show({
        severity: "info",
        summary: "Verification code issued",
        detail: `Fee ${formatAmountWithCommas(result.fee, result.currency)} · Recipient gets ${formatAmountWithCommas(
          result.net,
          result.currency
        )}. Code expires at ${new Date(result.expiresAt).toLocaleTimeString()}.`,
        life: 8000,
      });
    } catch (error: any) {
      resetVerification();
      toast.current?.show({
        severity: "error",
        summary: "Verification failed",
        detail: error.message || "Could not request a verification code.",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const pollUntilTerminal = (transferLocalId: number) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const transfer = await MoneyTransferService.getTransfer(transferLocalId);
        setLiveState(transfer.state);
        if (MoneyTransferService.isTerminalState(transfer.state)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (transfer.state === "succeeded") {
            toast.current?.show({
              severity: "success",
              summary: "Transfer completed",
              detail: `${formatAmountWithCommas(transfer.amount, transfer.currency)} delivered to ${
                transfer.verifiedName || "recipient"
              }.`,
              life: 10000,
            });
          } else {
            setFailureReason(transfer.failureReason || null);
            toast.current?.show({
              severity: "error",
              summary: "Transfer failed",
              detail: transfer.failureReason || `Transfer ended with state: ${transfer.state}`,
              life: 10000,
            });
            fetchWalletBalance();
          }
          fetchRecords();
        }
      } catch (error) {
        // Keep polling until attempts run out.
      }
      if (attempts >= 40 && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 4000);
  };

  const submitWire = async (keyToUse: string) => {
    setIsSending(true);
    try {
      const transfer = await MoneyTransferService.wireTransfer({
        verificationCode: verification!.verificationCode,
        idempotencyKey: keyToUse,
        method,
        amount: amount!,
        currency: "UGX",
        counterparty,
        reference: reference.trim() || undefined,
        narration: narration.trim() || undefined,
        fee: verification?.fee ?? 0,
      });
      setLiveState(transfer.state || "initialized");
      fetchWalletBalance();
      fetchRecords();
      toast.current?.show({
        severity: "success",
        summary: "Transfer submitted",
        detail: `Payout dispatched to the ${
          method === "mobile_money" ? "mobile money network" : "bank"
        }. Tracking status…`,
      });
      if (transfer.transactionId) {
        pollUntilTerminal(transfer.transactionId);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Transfer not sent",
        detail: error.message || "Could not submit the transfer.",
        life: 12000,
      });
      // Outcome may be unknown (e.g. dropped connection); retrying below reuses
      // the same idempotency key so KaCyber can never pay out twice.
      setLiveState("unknown");
    } finally {
      setIsSending(false);
    }
  };

  const handleSendNow = () => {
    if (!verification) return;
    const key = idempotencyKey || generateUuid();
    setIdempotencyKey(key);
    submitWire(key);
  };

  const handleRetry = () => {
    if (!idempotencyKey || !verification) return;
    setLiveState(null);
    submitWire(idempotencyKey);
  };

  const resetForm = () => {
    setMsisdn("");
    setNetwork("MTN");
    setAccountName("");
    setBankCode(null);
    setAccountNumber("");
    setAmount(null);
    setReference("");
    setNarration("");
    resetVerification();
  };

  const verificationIsCurrent = !!verification && verifiedSignature === payloadSignature;
  const codeExpired = !!verification && new Date(verification.expiresAt).getTime() <= Date.now();
  const canSend = verificationIsCurrent && !codeExpired && verification.sufficient !== false && !!amount;

  const getStatusSeverity = (state: string | null | undefined) => {
    switch ((state || "").toLowerCase()) {
      case "succeeded":
        return "success";
      case "failed":
      case "rejected":
      case "cancelled":
        return "danger";
      default:
        return "info";
    }
  };

  const breadcrumbHome = {
    icon: "pi pi-home",
    command: () => {
      window.location.href = HOME_ROUTE_PATH;
    },
  };

  const recipientBodyTemplate = (rowData: any) => (
    <div>
      <div className="font-bold">{rowData.accountName || rowData.verifiedName || rowData.msisdn}</div>
      <small className="text-500">
        {rowData.method === "bank" ? `${rowData.bankCode} · ${rowData.accountNumber}` : rowData.msisdn}
      </small>
    </div>
  );

  return (
    <div className="grid">
      <div className="col-12">
        <BreadCrumb
          home={breadcrumbHome}
          model={[
            { label: "Wallet", command: () => (window.location.href = WALLET_ROUTE_PATH) },
            { label: "Mobile & Bank Transfers" },
          ]}
        />
        <h5>Send Money — MTN / Airtel & Bank</h5>
        <Messages ref={message} style={{ width: "100%" }} />
        <Toast ref={toast} />

        {statsLoading ? (
          <div className="grid mb-3">
            <div className="col-12">
              <small className="text-500">Loading transfer statistics…</small>
            </div>
          </div>
        ) : (
          <div className="grid mb-3">
            <div className="col-12 md:col-3">
              <div className="flex flex-column align-items-center justify-content-center p-3" style={{ background: "#f4f6f9", borderRadius: "8px" }}>
                <span className="text-500 font-bold">Wallet Balance</span>
                <span className="text-2xl font-bold mt-2" style={{ color: "#4b79a1" }}>
                  {formatAmountWithCommas(walletBalance, "UGX")}
                </span>
                <span className="text-sm mt-1 text-center" style={{ color: "#6c757d" }}>
                  Transfers are funded from your balance. The fee is deducted from the amount.
                </span>
              </div>
            </div>
            <div className="col-12 md:col-3">
              <div className="flex flex-column align-items-center justify-content-center p-3" style={{ background: "#e8f5e9", borderRadius: "8px" }}>
                <span className="text-500 font-bold">Mobile Money</span>
                <span className="text-2xl font-bold mt-2" style={{ color: "#2e7d32" }}>
                  {mobileMoneyStats?.succeeded || 0}
                </span>
                <small className="text-sm text-center" style={{ color: "#6c757d" }}>
                  succeeded · {formatAmountWithCommas(mobileMoneyStats?.succeededAmount || 0, "UGX")}
                </small>
                <small className="text-sm text-center" style={{ color: "#6c757d" }}>
                  {mobileMoneyStats?.failed || 0} failed · {mobileMoneyStats?.pending || 0} pending
                </small>
              </div>
            </div>
            <div className="col-12 md:col-3">
              <div className="flex flex-column align-items-center justify-content-center p-3" style={{ background: "#e3f2fd", borderRadius: "8px" }}>
                <span className="text-500 font-bold">Bank Transfers</span>
                <span className="text-2xl font-bold mt-2" style={{ color: "#1565c0" }}>
                  {bankTransferStats?.succeeded || 0}
                </span>
                <small className="text-sm text-center" style={{ color: "#6c757d" }}>
                  succeeded · {formatAmountWithCommas(bankTransferStats?.succeededAmount || 0, "UGX")}
                </small>
                <small className="text-sm text-center" style={{ color: "#6c757d" }}>
                  {bankTransferStats?.failed || 0} failed · {bankTransferStats?.pending || 0} pending
                </small>
              </div>
            </div>
            <div className="col-12 md:col-3">
              <div className="flex flex-column align-items-center justify-content-center p-3" style={{ background: "#fff3e0", borderRadius: "8px" }}>
                <span className="text-500 font-bold">Total Transferred</span>
                <span className="text-2xl font-bold mt-2" style={{ color: "#ef6c00" }}>
                  {formatAmountWithCommas((mobileMoneyStats?.totalAmount || 0) + (bankTransferStats?.totalAmount || 0), "UGX")}
                </span>
                <small className="text-sm text-center" style={{ color: "#6c757d" }}>
                  {(mobileMoneyStats?.totalTransactions || 0) + (bankTransferStats?.totalTransactions || 0)} transactions
                </small>
              </div>
            </div>
          </div>
        )}

        <div className="grid mb-3">
          <div className="col-12 md:col-4">
            <div
              className="flex flex-column align-items-center justify-content-center p-3"
              style={{ background: "#f4f6f9", borderRadius: "8px" }}
            >
              <span className="text-500 font-bold">Wallet Balance</span>
              <span className="text-2xl font-bold mt-2" style={{ color: "#4b79a1" }}>
                {formatAmountWithCommas(walletBalance, "UGX")}
              </span>
              <span className="text-sm mt-1 text-center" style={{ color: "#6c757d" }}>
                Transfers are funded from your balance. The fee is deducted from the amount.
              </span>
            </div>
          </div>

          <div className="col-12 md:col-8">
            <div className="card">
              <h6 className="mb-3">Step 1 — Verify the transfer</h6>
              <div className="grid">
                <div className="col-12 md:col-6">
                  <label htmlFor="method">Destination</label>
                  <Dropdown
                    id="method"
                    value={method}
                    options={METHODS}
                    onChange={(e) => {
                      setMethod(e.value);
                      resetVerification();
                    }}
                    placeholder="Select destination"
                    className="w-full mt-1"
                    disabled={isSending}
                  />
                </div>

                <div className="col-12 md:col-6">
                  <label htmlFor="amount">Amount (UGX)</label>
                  <InputNumber
                    id="amount"
                    value={amount}
                    onValueChange={(e) => {
                      setAmount(Math.round(e.value || 0));
                      resetVerification();
                    }}
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={0}
                    min={500}
                    placeholder="e.g., 20000"
                    className="w-full mt-1"
                    disabled={isSending}
                  />
                </div>

                {method === "mobile_money" ? (
                  <>
                    <div className="col-12 md:col-4">
                      <label htmlFor="msisdn">Mobile Number</label>
                      <InputText
                        id="msisdn"
                        value={msisdn}
                        onChange={(e) => {
                          setMsisdn(e.target.value);
                          resetVerification();
                        }}
                        placeholder="256776521157"
                        className="w-full mt-1"
                        disabled={isSending}
                      />
                      {detectedNetwork && (
                        <small className="block mt-1" style={{ color: "#4b79a1", fontWeight: 500 }}>
                          Detected: {detectedNetwork.network}
                          {detectedNetwork.countryName ? ` (${detectedNetwork.countryName})` : ""}
                        </small>
                      )}
                    </div>
                    <div className="col-12 md:col-4">
                      <label htmlFor="network">Network</label>
                      <Dropdown
                        id="network"
                        value={network}
                        options={NETWORKS}
                        onChange={(e) => {
                          setNetwork(e.value);
                          resetVerification();
                        }}
                        placeholder="Select network"
                        className="w-full mt-1"
                        disabled={isSending}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-12 md:col-4">
                      <label htmlFor="bank">Bank</label>
                      <Dropdown
                        id="bank"
                        value={bankCode}
                        options={banks}
                        optionLabel="name"
                        optionValue="code"
                        filter
                        onChange={(e) => {
                          setBankCode(e.value);
                          resetVerification();
                        }}
                        placeholder="Select bank"
                        className="w-full mt-1"
                        disabled={isSending}
                      />
                    </div>
                    <div className="col-12 md:col-4">
                      <label htmlFor="accountNumber">Account Number</label>
                      <InputText
                        id="accountNumber"
                        value={accountNumber}
                        onChange={(e) => {
                          setAccountNumber(e.target.value);
                          resetVerification();
                        }}
                        placeholder="9030026525992"
                        className="w-full mt-1"
                        disabled={isSending}
                      />
                    </div>
                  </>
                )}

                <div className="col-12 md:col-4">
                  <label htmlFor="accountName">Recipient Name</label>
                  <InputText
                    id="accountName"
                    value={accountName}
                    onChange={(e) => {
                      setAccountName(e.target.value);
                      resetVerification();
                    }}
                    placeholder="Jane Doe"
                    className="w-full mt-1"
                    disabled={isSending}
                  />
                </div>

                <div className="col-12 md:col-6">
                  <label htmlFor="reference">Your Reference (optional)</label>
                  <InputText
                    id="reference"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="SETTLE-2026-07-30"
                    className="w-full mt-1"
                    disabled={isSending}
                  />
                </div>

                <div className="col-12 md:col-6">
                  <label htmlFor="narration">Narration (optional)</label>
                  <InputText
                    id="narration"
                    value={narration}
                    onChange={(e) => {
                      setNarration(e.target.value);
                      resetVerification();
                    }}
                    placeholder="Weekly settlement"
                    className="w-full mt-1"
                    disabled={isSending}
                  />
                </div>

                <div className="col-12 flex justify-content-end mt-2">
                  <Button
                    label="Get Verification Code"
                    icon={PrimeIcons.SHIELD}
                    className="p-button-secondary"
                    onClick={handleRequestVerification}
                    loading={isVerifying}
                    disabled={isSending}
                  />
                </div>
              </div>

              <Divider />

              <h6 className="mb-3">Step 2 — Wire the transfer</h6>
              {!verificationIsCurrent ? (
                <p className="text-500 mt-0">
                  Request a verification code first. We confirm your wallet covers the amount plus the charge
                  and issue a single-use code valid for 15 minutes.
                </p>
              ) : (
                <div className="grid">
                  <div className="col-12 lg:col-6">
                    <div className="p-3" style={{ background: "#f4f6f9", borderRadius: "8px" }}>
                      <div className="flex justify-content-between mb-2">
                        <span className="text-500">Verification Code</span>
                        <span className="font-bold">{verification.verificationCode}</span>
                      </div>
                      <div className="flex justify-content-between mb-2">
                        <span className="text-500">Expires At</span>
                        <span>{new Date(verification.expiresAt).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-content-between mb-2">
                        <span className="text-500">Fee</span>
                        <span>{formatAmountWithCommas(verification.fee, verification.currency)}</span>
                      </div>
                      <div className="flex justify-content-between mb-2">
                        <span className="text-500">Recipient Receives (net)</span>
                        <span className="font-bold">
                          {formatAmountWithCommas(verification.net, verification.currency)}
                        </span>
                      </div>
                      <div className="flex justify-content-between">
                        <span className="text-500">Balance Sufficient</span>
                        <i
                          className={
                            verification.sufficient ? "pi pi-check-circle text-green-500" : "pi pi-times-circle text-red-500"
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-12 lg:col-6">
                    {(liveState || failureReason) && (
                      <div className="mb-3 p-3" style={{ background: "#f4f6f9", borderRadius: "8px" }}>
                        <div className="flex justify-content-between align-items-center mb-1">
                          <span className="text-500">Status</span>
                          <Tag value={(liveState || "pending").toUpperCase()} severity={getStatusSeverity(liveState)} />
                        </div>
                        {failureReason && (
                          <div className="text-red-500 text-sm">{failureReason}</div>
                        )}
                        {liveState === "unknown" && (
                          <small className="block mt-1">
                            Outcome unknown. Retry safely below — the same idempotency key returns the original
                            transfer, never a duplicate payment.
                          </small>
                        )}
                      </div>
                    )}
                    {verification.sufficient === false ? (
                      <div className="text-orange-500 mb-2">
                        Your wallet cannot cover this amount yet. Top up or lower the amount.
                      </div>
                    ) : null}
                    <Button
                      label={`Send ${formatAmountWithCommas(amount || 0, "UGX")}`}
                      icon={PrimeIcons.SEND}
                      className="p-button-success w-full"
                      onClick={handleSendNow}
                      loading={isSending}
                      disabled={!canSend}
                    />
                    {liveState === "unknown" && (
                      <Button
                        label="Retry with same key"
                        icon={PrimeIcons.REFRESH}
                        className="p-button-warning w-full mt-2"
                        onClick={handleRetry}
                        loading={isSending}
                      />
                    )}
                    <small className="block mt-2 text-500">
                      The recipient receives {formatAmountWithCommas(verification.net, verification.currency)} (amount minus
                      the fee). Changing any detail invalidates the code.
                    </small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Transfer History Section */}
        <div className="card">
          <div className="flex align-items-center justify-content-between mb-3">
            <h6>Transfer History</h6>
            <Button
              label="Refresh"
              icon={PrimeIcons.REFRESH}
              className="p-button-info p-button-sm"
              onClick={() => {
                fetchRecords();
                fetchWalletBalance();
                fetchTransferStats();
              }}
            />
          </div>
          <DataTable
            value={records}
            lazy
            paginator
            rows={limit}
            totalRecords={totalRecords}
            loading={loading}
            first={offset}
            rowsPerPageOptions={[5, 10, 25, 50]}
            onPage={(event: any) => setOffset(event.first)}
            dataKey="id"
            emptyMessage="No transfers found"
          >
            <Column field="id" header="#" headerStyle={{ width: "60px" }} />
            <Column header="Recipient" body={recipientBodyTemplate} />
            <Column field="reference" header="Reference" />
            <Column
              field="amount"
              header="Amount"
              body={(rowData: any) => formatAmountWithCommas(rowData.amount, rowData.currency || "UGX")}
            />
            <Column
              field="fee"
              header="Fee"
              body={(rowData: any) => formatAmountWithCommas(rowData.fee || 0, rowData.currency || "UGX")}
            />
            <Column
              field="state"
              header="Status"
              body={(rowData: any) => (
                <Tag value={(rowData.state || "").toUpperCase()} severity={getStatusSeverity(rowData.state)} />
              )}
            />
            <Column
              field="dateCreated"
              header="Date"
              body={(rowData: any) => rowData.dateCreated?.substring(0, 10)}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default MoneyTransfers;
